import { useMemo, useCallback, MutableRefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { DropdownMenuTextOption } from '@alltrails/denali/types';
import { useValueRef } from '@alltrails/core';
import Add from '@alltrails/denali/icons/Add';
import PlaceFilled from '@alltrails/denali/icons/PlaceFilled';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import { useDispatch, useSelector } from '../../../redux';
import { setWaypointEditingState, clearActiveWaypointPopup } from '../../../redux/customRoute';
import useRouteToPoint from '../DrawControls/useRouteToPoint';
import WaypointContextMenu from './WaypointContextMenu';

const WaypointRoutingOptions = () => {
  const dispatch = useDispatch();
  const { activeWaypointPopupId, waypoints, isSmartRouting } = useSelector(state => ({
    activeWaypointPopupId: state.customRoute.activeWaypointPopupId,
    waypoints: state.customRoute.route.waypoints,
    isSmartRouting: state.customRoute.isSmartRouting
  }));
 
  const isSmartRoutingRef = useValueRef(isSmartRouting) as MutableRefObject<boolean>;
  const { routeToPoint } = useRouteToPoint(isSmartRoutingRef);

  const activeWaypoint = useMemo(() => {
    if (activeWaypointPopupId === undefined) return undefined;
    return waypoints.find(w => w.id === activeWaypointPopupId);
  }, [activeWaypointPopupId, waypoints]);

  const handleOpenWaypoint = useCallback(() => {
    if (activeWaypointPopupId !== undefined) {
      dispatch(setWaypointEditingState({ selectedId: activeWaypointPopupId }));
      dispatch(clearActiveWaypointPopup());
      logCustomRouteUpdated({ entry_point_location: 'waypoint clicked' });
    }
  }, [dispatch, activeWaypointPopupId]);

  const handleAddToRoute = useCallback(async () => {
    if (activeWaypoint && activeWaypointPopupId !== undefined) {
      await routeToPoint({ lat: activeWaypoint.latitude, lng: activeWaypoint.longitude }, false, true);
      dispatch(clearActiveWaypointPopup());
       
    }
  }, [activeWaypoint, activeWaypointPopupId, routeToPoint, dispatch]);

  const popupOptions = useMemo<DropdownMenuTextOption[]>(
    () => [
      {
        testId: 'waypoint-context-menu-open',
        leadingIcon: { icon: { Component: PlaceFilled, size: 'xs' } },
        title: <FormattedMessage defaultMessage="Open waypoint" />,
        onClick: handleOpenWaypoint
      },
      {
        testId: 'waypoint-context-menu-add-to-route',
        leadingIcon: { icon: { Component: Add, size: 'xs' } },
        title: <FormattedMessage defaultMessage="Add to route" />,
        onClick: handleAddToRoute
      }
    ],
    [handleOpenWaypoint, handleAddToRoute]
  );

  return <>{activeWaypoint && <WaypointContextMenu options={popupOptions} waypoint={activeWaypoint} />}</>;
};

export default WaypointRoutingOptions;
